/**
 * AuthPage.js — Login and sign-up screen
 *
 * Provides a simple local authentication flow for the demo app.
 * Login validates against users stored in the in-memory data store.
 * Sign up is prepared to call the backend create-user endpoint directly.
 */

import React, { useState, useRef, useEffect } from 'react';
import { LogIn, UserPlus } from 'lucide-react';
import { fleetApi } from '../api/fleetApi';
import { eRole } from '../constants/roles';
import { useLanguage } from '../i18n/LanguageContext';
import { useTrackAsync } from '../hooks/useBlockingTracker';
import { normalizeApiError } from '../utils/validate';

// ── Live operations map ──────────────────────────────────────────────
// The background is the product's own subject: a road network with vehicles
// moving along it. GEO8 tracks fleets, so the login screen shows a fleet
// moving rather than an abstract texture.
//
// Canvas, not SVG/DOM — ~18 moving objects cost one repaint per frame instead
// of 18 style recalculations. The network is generated from a fixed seed, so
// the layout is identical on every load: it reads as a place you return to,
// not a different random smear each visit.
//
// Motion is deliberately slow (14-40 px/sec) and low-contrast. It should
// register as "this system is live" in peripheral vision and never pull focus
// from the sign-in form. Honours prefers-reduced-motion by painting one
// static frame and never starting the loop.
function AuthMap() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas && canvas.getContext ? canvas.getContext('2d') : null;
    if (!ctx) return undefined;

    const reduceMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const INK = '26, 24, 22';
    const ACCENT = '232, 130, 12';

    let width = 0;
    let height = 0;
    let minorRoads = [];
    let arterials = [];
    let highways = [];
    let vehicles = [];
    let pins = [];
    let rafId = null;
    let startedAt = 0;
    let lastNow = 0;

    // Deterministic PRNG so the map is the same on every load.
    const seeded = (initial) => {
      let s = initial;
      return () => {
        s = (s * 1103515245 + 12345) & 0x7fffffff;
        return s / 0x7fffffff;
      };
    };

    // Precompute segment lengths so a vehicle can be placed by distance
    // travelled rather than by re-walking the polyline every frame.
    const buildPath = (pts) => {
      const segs = [];
      let total = 0;
      for (let i = 0; i < pts.length - 1; i++) {
        const dx = pts[i + 1][0] - pts[i][0];
        const dy = pts[i + 1][1] - pts[i][1];
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len === 0) continue;
        segs.push({ x: pts[i][0], y: pts[i][1], dx, dy, len, start: total });
        total += len;
      }
      return { pts, segs, total };
    };

    const pointAt = (path, dist) => {
      if (!path.total || !path.segs.length) return { x: 0, y: 0 };
      let d = dist % path.total;
      if (d < 0) d += path.total;
      for (let i = 0; i < path.segs.length; i++) {
        const s = path.segs[i];
        if (d <= s.start + s.len) {
          const t = (d - s.start) / s.len;
          return { x: s.x + s.dx * t, y: s.y + s.dy * t };
        }
      }
      const s = path.segs[path.segs.length - 1];
      return { x: s.x + s.dx, y: s.y + s.dy };
    };

    const build = () => {
      const rand = seeded(4071981);
      minorRoads = [];
      arterials = [];
      highways = [];
      pins = [];

      // Three tiers, drawn at different weights. A real map reads as a map
      // because of that hierarchy — a single uniform grid just reads as
      // graph paper, which is what the first attempt looked like.
      const H_COUNT = 8;
      const V_COUNT = 10;
      const hAt = [];
      const vAt = [];

      // Arterials: full-span and gently wavy, never ruler-straight.
      for (let i = 0; i < H_COUNT; i++) {
        const y = ((i + 0.5) / H_COUNT) * height;
        const pts = [];
        for (let s = 0; s <= 10; s++) {
          pts.push([(s / 10) * width, y + (rand() - 0.5) * height * 0.035]);
        }
        arterials.push(buildPath(pts));
        hAt.push(y);
      }
      for (let i = 0; i < V_COUNT; i++) {
        const x = ((i + 0.5) / V_COUNT) * width;
        const pts = [];
        for (let s = 0; s <= 8; s++) {
          pts.push([x + (rand() - 0.5) * width * 0.02, (s / 8) * height]);
        }
        arterials.push(buildPath(pts));
        vAt.push(x);
      }

      // Minor streets subdivide each block. These carry most of the "city"
      // feel; they stop short of the edges so the grid doesn't look printed.
      for (let i = 0; i < H_COUNT - 1; i++) {
        const count = 1 + Math.floor(rand() * 2);
        for (let k = 0; k < count; k++) {
          const y = hAt[i] + (hAt[i + 1] - hAt[i]) * ((k + 1) / (count + 1));
          minorRoads.push(buildPath([
            [rand() * width * 0.3, y],
            [width - rand() * width * 0.3, y],
          ]));
        }
      }
      for (let i = 0; i < V_COUNT - 1; i++) {
        const count = 1 + Math.floor(rand() * 2);
        for (let k = 0; k < count; k++) {
          const x = vAt[i] + (vAt[i + 1] - vAt[i]) * ((k + 1) / (count + 1));
          minorRoads.push(buildPath([
            [x, rand() * height * 0.3],
            [x, height - rand() * height * 0.3],
          ]));
        }
      }

      // Highways: long sweeping diagonals that cut across the grid. These are
      // what stop the composition reading as a plain lattice.
      for (let i = 0; i < 3; i++) {
        const y0 = rand() * height;
        const y1 = rand() * height;
        const pts = [];
        for (let s = 0; s <= 14; s++) {
          const t = s / 14;
          pts.push([
            t * width,
            y0 + (y1 - y0) * t + Math.sin(t * Math.PI * 1.6 + i * 2) * height * 0.07,
          ]);
        }
        highways.push(buildPath(pts));
      }

      // Vehicle count scales with viewport so density stays even. Highways
      // get the faster traffic, arterials the slower — same as real roads.
      const routes = highways.concat(arterials);
      const count = Math.max(12, Math.min(26, Math.round(width / 72)));
      vehicles = [];
      for (let i = 0; i < count; i++) {
        const onHighway = i < 3 || rand() > 0.62;
        const pool = onHighway ? highways : arterials;
        const path = pool[Math.floor(rand() * pool.length)] || routes[0];
        vehicles.push({
          path,
          dist: rand() * path.total,
          speed: (onHighway ? 30 + rand() * 26 : 14 + rand() * 18) * (rand() > 0.5 ? 1 : -1),
        });
      }

      // A few stationary depots, slowly breathing.
      for (let i = 0; i < 5; i++) {
        const path = arterials[Math.floor(rand() * arterials.length)];
        const p = pointAt(path, rand() * path.total);
        pins.push({ x: p.x, y: p.y, phase: rand() * Math.PI * 2 });
      }
    };

    const strokePath = (path, color, lineWidth) => {
      if (!path.pts.length) return;
      ctx.beginPath();
      ctx.moveTo(path.pts[0][0], path.pts[0][1]);
      for (let i = 1; i < path.pts.length; i++) ctx.lineTo(path.pts[i][0], path.pts[i][1]);
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.stroke();
    };

    const draw = (elapsed) => {
      ctx.clearRect(0, 0, width, height);
      ctx.lineCap = 'round';

      minorRoads.forEach((r) => strokePath(r, `rgba(${INK}, 0.05)`, 1));
      arterials.forEach((r) => strokePath(r, `rgba(${INK}, 0.095)`, 1.3));
      // Highways get a pale casing under the line, the way printed maps
      // outline major routes — it separates them from the grid beneath.
      highways.forEach((r) => strokePath(r, 'rgba(248, 247, 245, 0.95)', 7));
      highways.forEach((r) => strokePath(r, `rgba(${INK}, 0.15)`, 2.2));

      pins.forEach((pin) => {
        const pulse = (Math.sin(elapsed * 0.0014 + pin.phase) + 1) / 2;
        ctx.beginPath();
        ctx.arc(pin.x, pin.y, 7 + pulse * 10, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${ACCENT}, ${0.09 * (1 - pulse)})`;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(pin.x, pin.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${ACCENT}, 0.45)`;
        ctx.fill();
      });

      vehicles.forEach((v) => {
        // Tapered trail sampled back along the path, so it bends with corners
        // instead of trailing off straight behind the vehicle.
        //
        // The guard matters: pointAt() wraps with modulo, so once a vehicle
        // passes the end of its route the trail samples wrap to the far end
        // and the segment gets drawn as a long orange streak straight across
        // the map. Skipping segments whose sample falls outside the path
        // simply truncates the trail for the moment of the wrap, which is
        // imperceptible at this scale.
        const dir = v.speed >= 0 ? 1 : -1;
        const total = v.path.total;
        for (let k = 6; k >= 1; k--) {
          const da = v.dist - dir * k * 7;
          const db = v.dist - dir * (k - 1) * 7;
          if (da < 0 || da > total || db < 0 || db > total) continue;
          const a = pointAt(v.path, da);
          const b = pointAt(v.path, db);
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.strokeStyle = `rgba(${ACCENT}, ${0.04 + (7 - k) * 0.018})`;
          ctx.lineWidth = 0.8 + (7 - k) * 0.2;
          ctx.stroke();
        }
        const p = pointAt(v.path, v.dist);
        ctx.beginPath();
        ctx.arc(p.x, p.y, 7.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${ACCENT}, 0.14)`;
        ctx.fill();
        // White collar keeps the vehicle legible where it crosses a road.
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3.9, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 254, 252, 0.95)';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(p.x, p.y, 2.7, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${ACCENT}, 0.95)`;
        ctx.fill();
      });
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      if (!width || !height) return;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
      if (reduceMotion) draw(0);
    };

    const frame = (now) => {
      if (!startedAt) startedAt = now;
      // Delta-timed so speed is identical on 60Hz and 120Hz displays.
      const dt = lastNow ? Math.min((now - lastNow) / 1000, 0.05) : 0;
      lastNow = now;
      for (let i = 0; i < vehicles.length; i++) {
        const v = vehicles[i];
        v.dist += v.speed * dt;
        // Keep dist inside [0, total) rather than letting it grow unbounded,
        // so the trail's wrap guard above can actually detect the wrap.
        if (v.path.total > 0) {
          v.dist %= v.path.total;
          if (v.dist < 0) v.dist += v.path.total;
        }
      }
      draw(now - startedAt);
      rafId = window.requestAnimationFrame(frame);
    };

    resize();
    if (!reduceMotion) rafId = window.requestAnimationFrame(frame);
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, []);

  return <canvas ref={canvasRef} className="auth-map" aria-hidden="true" />;
}

const loginDefaults = { identifier: '', password: '' };
const signupDefaults = {
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  mobilePhoneNumber: '',
  companyName: '',
  signupCode: '',
};

export default function AuthPage({ onLogin, onFirstLogin }) {
  const { t } = useLanguage();
  const trackAsync = useTrackAsync();
  const [mode, setMode] = useState('login');
  const [loginForm, setLoginForm] = useState(loginDefaults);
  const [signupForm, setSignupForm] = useState(signupDefaults);
  const [error, setError] = useState('');
  const [signupErrors, setSignupErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLogin = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');

     try {
      const result = await trackAsync(
        fleetApi.auth.login({
          emailOrUsername: loginForm.identifier.trim(),
          password: loginForm.password,
        })
      );

      // First-time login: status 1 means the user must set a new password
      if (result.userState === 1) {
        onFirstLogin?.({
          user: {
            id: result.id ?? result.userId,
            username: result.userName || loginForm.identifier.trim(),
            organizationId: result.organizationId,
          },
          token: result.token,
          rawPassword: loginForm.password,
        });
        return;
      }

      const userId = result.id ?? result.userId;
      let roleName = result.roleName || result.role || result.userRole || '';
      if (!roleName && userId && result.token) {
        try {
          const roles = await fleetApi.roles.getByUserId(userId, result.token);
          const first = Array.isArray(roles) ? roles[0] : roles?.$values?.[0] ?? roles?.data?.[0];
          roleName = first?.name || first?.roleName || first?.title || '';
        } catch { /* non-critical — fall back to empty */ }
      }

      const resolvedUser = {
        id: userId,
        fullName: result.userName || loginForm.identifier.trim(),
        email: loginForm.identifier.trim(),
        username: result.userName || loginForm.identifier.trim(),
        phone: '',
        role: roleName || 'user',
        department: 'Operations',
        status: result.userState === 0 ? 'inactive' : 'active',
        organizationId: result.organizationId,
        companyName: result.organizationName,
        lastLoginAt: new Date().toISOString(),
      };

      onLogin({
        token: result.token,
        user: resolvedUser,
        organizationId: result.organizationId,
        organizationName: result.organizationName,
        expiresAtUtc: result.expiresAtUtc,
      });
    } catch (loginError) {
      setError(normalizeApiError(loginError, 'sign in', { context: 'login' }).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignup = (event) => {
    event.preventDefault();
    const createUser = async () => {
      const errs = {};
      if (!signupForm.username.trim())        errs.username        = 'Username is required.';
      if (!signupForm.email.trim())           errs.email           = 'Email is required.';
      if (!signupForm.firstName.trim())       errs.firstName       = 'First name is required.';
      if (!signupForm.lastName.trim())        errs.lastName        = 'Last name is required.';
      if (!signupForm.phoneNumber.trim())     errs.phoneNumber     = 'Phone number is required.';
      if (!signupForm.mobilePhoneNumber.trim()) errs.mobilePhoneNumber = 'Mobile phone number is required.';
      if (!signupForm.companyName.trim())     errs.companyName     = 'Organization name is required.';
      // Required here because self-service signup always creates a new
      // organization (no organizationId is sent below). Only an invite-style
      // flow that already knows the organization may omit it.
      if (!signupForm.signupCode.trim())      errs.signupCode      = 'Signup code is required.';
      if (!signupForm.password)               errs.password        = 'Password is required.';
      if (!signupForm.confirmPassword)        errs.confirmPassword = 'Please confirm your password.';
      else if (signupForm.password !== signupForm.confirmPassword) errs.confirmPassword = 'Passwords do not match.';

      if (Object.keys(errs).length) { setSignupErrors(errs); return; }
      setSignupErrors({});

      setIsSubmitting(true);
      setError('');

      try {
        const created = await fleetApi.users.create({
          username: signupForm.username.trim(),
          email: signupForm.email.trim().toLowerCase(),
          password: signupForm.password,
          firstName: signupForm.firstName.trim(),
          lastName: signupForm.lastName.trim(),
          phoneNumber: signupForm.phoneNumber.trim(),
          mobilePhoneNumber: signupForm.mobilePhoneNumber.trim(),
          companyName: signupForm.companyName.trim(),
          // Trimmed and upper-cased to match what the field displays; the server
          // normalises the same way, so case and stray whitespace are forgiven.
          signupCode: signupForm.signupCode.trim().toUpperCase(),
          roleId: eRole.SuperAdmin,
          statusId: 2,
          // No organizationId: the backend reads "absent or 0" as "create a new
          // organization for this user" and adds them to it via OrganizationUsers.
          // Role rows no longer carry an organization, and a SuperAdmin is
          // unscoped, so no fleet or zone ids are sent either.
          viewList: [],
        });
        const createdUserId = created?.id ?? created?.userId;

        const loginResult = await fleetApi.auth.login({
          emailOrUsername: signupForm.email.trim().toLowerCase(),
          password: signupForm.password,
        });
        // Immediately activate the superadmin — they chose their own password at signup,
        // so the backend's default statusId=1 (New / first-login) is not appropriate.
        if (createdUserId && loginResult.token) {
          await fleetApi.users.updateUserStatus(createdUserId, 2, loginResult.token).catch(() => {});
        }

        const resolvedUser = {
          id: createdUserId ?? loginResult.id ?? loginResult.userId,
          fullName: `${signupForm.firstName.trim()} ${signupForm.lastName.trim()}`.trim(),
          email: signupForm.email.trim().toLowerCase(),
          username: signupForm.username.trim(),
          phone: signupForm.mobilePhoneNumber.trim() || signupForm.phoneNumber.trim(),
          role: 'superadmin',
          department: 'Operations',
          status: 'active',
          organizationId: loginResult.organizationId,
          companyName: loginResult.organizationName || signupForm.companyName.trim(),
          lastLoginAt: new Date().toISOString(),
        };

        onLogin({
          token: loginResult.token,
          user: resolvedUser,
          organizationId: loginResult.organizationId,
          organizationName: loginResult.organizationName || signupForm.companyName.trim(),
        });
        setSignupForm(signupDefaults);
      } catch (signupError) {
        setError(normalizeApiError(signupError, 'create account').message);
      } finally {
        setIsSubmitting(false);
      }
    };

    createUser();
  };

  return (
    <div className="auth-shell">
      <AuthMap />

      <div className="auth-panel">
        <div className="auth-brand-band">
          <div className="auth-brand-inner">
            <div className="auth-brand-name">GEO <span className="auth-brand-name-accent">8</span></div>
            <h1>{t('fleetMgmtSystem')}</h1>
          </div>
        </div>

        <div className="auth-card">
          <div className="auth-tabs">
            <button className={`auth-tab ${mode === 'login' ? 'active' : ''}`} onClick={() => { setMode('login'); setError(''); }}>
              <LogIn size={14} />
              {t('login')}
            </button>
            <button className={`auth-tab ${mode === 'signup' ? 'active' : ''}`} onClick={() => { setMode('signup'); setError(''); }}>
              <UserPlus size={14} />
              {t('signUp')}
            </button>
          </div>

          {mode === 'login' ? (
            <form className="auth-form" onSubmit={handleLogin}>
              <div className="auth-form-head">
                <div className="auth-form-title">{t('welcomeBack')}</div>
                <div className="auth-form-sub">Sign in to your operation</div>
              </div>
              <div className="form-group">
                <label>{t('usernameOrEmail')}</label>
                <input
                  value={loginForm.identifier}
                  onChange={event => setLoginForm(prev => ({ ...prev, identifier: event.target.value }))}
                  placeholder={t('enterUsernameEmail')}
                />
              </div>
              <div className="form-group">
                <label>{t('password')}</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={event => setLoginForm(prev => ({ ...prev, password: event.target.value }))}
                  placeholder={t('enterPassword')}
                />
              </div>
              {error && <div className="auth-error">{error}</div>}
              <button className="btn btn-primary auth-submit" type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('signingIn') : t('login')}
              </button>
            </form>
          ) : (
            <form className="auth-form" onSubmit={handleSignup}>
              <div>
                <div className="section-title">{t('createAccount')}</div>
              </div>
              <div className="form-group">
                <label>{t('username')} *</label>
                <input value={signupForm.username} onChange={event => { setSignupForm(prev => ({ ...prev, username: event.target.value })); if (signupErrors.username) setSignupErrors(p => ({ ...p, username: undefined })); }} className={signupErrors.username ? 'input-error' : ''} />
                {signupErrors.username && <span className="field-error">{signupErrors.username}</span>}
              </div>
              <div className="form-group">
                <label>{t('email')} *</label>
                <input type="email" value={signupForm.email} onChange={event => { setSignupForm(prev => ({ ...prev, email: event.target.value })); if (signupErrors.email) setSignupErrors(p => ({ ...p, email: undefined })); }} className={signupErrors.email ? 'input-error' : ''} />
                {signupErrors.email && <span className="field-error">{signupErrors.email}</span>}
              </div>
              <div className="form-group">
                <label>{t('firstName')} *</label>
                <input value={signupForm.firstName} onChange={event => { setSignupForm(prev => ({ ...prev, firstName: event.target.value })); if (signupErrors.firstName) setSignupErrors(p => ({ ...p, firstName: undefined })); }} className={signupErrors.firstName ? 'input-error' : ''} />
                {signupErrors.firstName && <span className="field-error">{signupErrors.firstName}</span>}
              </div>
              <div className="form-group">
                <label>{t('lastName')} *</label>
                <input value={signupForm.lastName} onChange={event => { setSignupForm(prev => ({ ...prev, lastName: event.target.value })); if (signupErrors.lastName) setSignupErrors(p => ({ ...p, lastName: undefined })); }} className={signupErrors.lastName ? 'input-error' : ''} />
                {signupErrors.lastName && <span className="field-error">{signupErrors.lastName}</span>}
              </div>
              <div className="form-group">
                <label>{t('phoneNumber')} *</label>
                <input value={signupForm.phoneNumber} onChange={event => { setSignupForm(prev => ({ ...prev, phoneNumber: event.target.value })); if (signupErrors.phoneNumber) setSignupErrors(p => ({ ...p, phoneNumber: undefined })); }} className={signupErrors.phoneNumber ? 'input-error' : ''} />
                {signupErrors.phoneNumber && <span className="field-error">{signupErrors.phoneNumber}</span>}
              </div>
              <div className="form-group">
                <label>{t('mobilePhoneNumber')} *</label>
                <input value={signupForm.mobilePhoneNumber} onChange={event => { setSignupForm(prev => ({ ...prev, mobilePhoneNumber: event.target.value })); if (signupErrors.mobilePhoneNumber) setSignupErrors(p => ({ ...p, mobilePhoneNumber: undefined })); }} className={signupErrors.mobilePhoneNumber ? 'input-error' : ''} />
                {signupErrors.mobilePhoneNumber && <span className="field-error">{signupErrors.mobilePhoneNumber}</span>}
              </div>
              <div className="form-group">
                <label>{t('companyName')} *</label>
                <input value={signupForm.companyName} onChange={event => { setSignupForm(prev => ({ ...prev, companyName: event.target.value })); if (signupErrors.companyName) setSignupErrors(p => ({ ...p, companyName: undefined })); }} placeholder={t('yourCompanyName')} className={signupErrors.companyName ? 'input-error' : ''} />
                {signupErrors.companyName && <span className="field-error">{signupErrors.companyName}</span>}
              </div>
              <div className="form-group">
                <label>{t('signupCode')} *</label>
                {/* Uppercasing is display-only; the value stays as typed and is
                    normalised at submit. No masking, dashes or format checks:
                    the code is matched exactly after trim/upper, and there is
                    deliberately no lookup endpoint to validate against — one
                    would let anyone probe which codes exist. */}
                <input
                  value={signupForm.signupCode}
                  onChange={event => {
                    setSignupForm(prev => ({ ...prev, signupCode: event.target.value }));
                    if (signupErrors.signupCode) setSignupErrors(p => ({ ...p, signupCode: undefined }));
                  }}
                  maxLength={12}
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  className={signupErrors.signupCode ? 'input-error' : ''}
                  style={{ fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em' }}
                />
                {signupErrors.signupCode
                  ? <span className="field-error">{signupErrors.signupCode}</span>
                  : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('signupCodeHint')}</span>}
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>{t('password')} *</label>
                  <input type="password" value={signupForm.password} onChange={event => { setSignupForm(prev => ({ ...prev, password: event.target.value })); if (signupErrors.password) setSignupErrors(p => ({ ...p, password: undefined })); }} className={signupErrors.password ? 'input-error' : ''} />
                  {signupErrors.password && <span className="field-error">{signupErrors.password}</span>}
                </div>
                <div className="form-group">
                  <label>{t('confirmPassword')} *</label>
                  <input type="password" value={signupForm.confirmPassword} onChange={event => { setSignupForm(prev => ({ ...prev, confirmPassword: event.target.value })); if (signupErrors.confirmPassword) setSignupErrors(p => ({ ...p, confirmPassword: undefined })); }} className={signupErrors.confirmPassword ? 'input-error' : ''} />
                  {signupErrors.confirmPassword && <span className="field-error">{signupErrors.confirmPassword}</span>}
                </div>
              </div>
              {error && <div className="auth-error">{error}</div>}
              <button className="btn btn-primary auth-submit" type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('creatingAccount') : t('createAccountBtn')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
